import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowRight,
  Scale,
  Calendar,
  User,
  Building,
  CheckCircle2,
  Edit,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CaseDetails({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [judgmentDetails, setJudgmentDetails] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchCase();
  }, [id]);

  const fetchCase = async () => {
    try {
      const response = await axios.get(`${API}/cases/${id}`);
      setCaseData(response.data);
      setNewStatus(response.data.status);
      setJudgmentDetails(response.data.judgment_details || '');
    } catch (error) {
      toast.error('فشل تحميل تفاصيل القضية');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) {
      toast.error('الرجاء اختيار الحالة');
      return;
    }

    setUpdating(true);
    try {
      const updateData = { status: newStatus };
      if (newStatus === 'final_judgment' && judgmentDetails) {
        updateData.judgment_details = judgmentDetails;
      }

      await axios.patch(`${API}/cases/${id}`, updateData);
      toast.success('تم تحديث حالة القضية بنجاح');
      setIsUpdateDialogOpen(false);
      fetchCase();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل تحديث القضية');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      new: { text: 'جديدة', class: 'status-new' },
      filed: { text: 'مرفوعة', class: 'status-filed' },
      hearings: { text: 'جلسات', class: 'status-hearings' },
      final_judgment: { text: 'حكم نهائي', class: 'status-final_judgment' },
      execution: { text: 'تنفيذ', class: 'status-execution' },
    };
    const statusInfo = statusMap[status] || statusMap.new;
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  if (!caseData) {
    return null;
  }

  const isLegalManager = user?.role === 'legal_manager';
  const canUpdate = isLegalManager;

  return (
    <div className="min-h-screen bg-slate-50 p-8" dir="rtl">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4" data-testid="back-button">
            <ArrowRight size={20} className="ml-2" />
            العودة
          </Button>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2">{caseData.title}</h1>
              <p className="text-slate-600 text-lg">رقم القضية: {caseData.case_number}</p>
            </div>
            {getStatusBadge(caseData.status)}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-right">معلومات القضية</CardTitle>
                <CardDescription className="text-right">التفاصيل الأساسية للقضية</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Scale className="text-slate-500" size={20} />
                    <div>
                      <p className="text-xs text-slate-500">نوع القضية</p>
                      <p className="font-medium text-slate-900">{caseData.case_type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <User className="text-slate-500" size={20} />
                    <div>
                      <p className="text-xs text-slate-500">المدعي</p>
                      <p className="font-medium text-slate-900">{caseData.plaintiff}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <User className="text-slate-500" size={20} />
                    <div>
                      <p className="text-xs text-slate-500">المدعى عليه</p>
                      <p className="font-medium text-slate-900">{caseData.defendant}</p>
                    </div>
                  </div>
                  {caseData.court_name && (
                    <div className="flex items-center gap-3">
                      <Building className="text-slate-500" size={20} />
                      <div>
                        <p className="text-xs text-slate-500">المحكمة</p>
                        <p className="font-medium text-slate-900">{caseData.court_name}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <Calendar className="text-slate-500" size={20} />
                    <div>
                      <p className="text-xs text-slate-500">تاريخ الإنشاء</p>
                      <p className="font-medium text-slate-900">
                        {new Date(caseData.created_at).toLocaleDateString('ar-SA')}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-xs text-slate-500 mb-2">الوصف</p>
                  <p className="text-slate-700">{caseData.description}</p>
                </div>

                {caseData.notes && (
                  <div className="pt-4 border-t border-slate-200">
                    <p className="text-xs text-slate-500 mb-2">ملاحظات</p>
                    <p className="text-slate-700">{caseData.notes}</p>
                  </div>
                )}

                {caseData.judgment_details && (
                  <div className="pt-4 border-t border-slate-200">
                    <p className="text-xs text-slate-500 mb-2">تفاصيل الحكم</p>
                    <p className="text-slate-700">{caseData.judgment_details}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            {isLegalManager && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-right">الإجراءات</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {canUpdate && (
                    <Button
                      onClick={() => setIsUpdateDialogOpen(true)}
                      className="w-full bg-[#E94B7B] hover:bg-[#d43a6a]"
                      data-testid="update-status-button"
                    >
                      <Edit size={18} className="ml-2" />
                      تحديث حالة القضية
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-right">المسار الزمني</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 size={16} className="text-slate-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">تم الإنشاء</p>
                      <p className="text-xs text-slate-500">
                        {new Date(caseData.created_at).toLocaleString('ar-SA')}
                      </p>
                    </div>
                  </div>
                  {caseData.filed_at && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 size={16} className="text-amber-700" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">تم رفع القضية</p>
                        <p className="text-xs text-slate-500">
                          {new Date(caseData.filed_at).toLocaleString('ar-SA')}
                        </p>
                      </div>
                    </div>
                  )}
                  {caseData.judgment_at && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-emerald-200 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 size={16} className="text-emerald-700" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">صدور الحكم النهائي</p>
                        <p className="text-xs text-slate-500">
                          {new Date(caseData.judgment_at).toLocaleString('ar-SA')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Dialog open={isUpdateDialogOpen} onOpenChange={setIsUpdateDialogOpen}>
        <DialogContent dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-right">تحديث حالة القضية</DialogTitle>
            <DialogDescription className="text-right">
              اختر الحالة الجديدة للقضية
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="status" className="text-right block">الحالة الجديدة *</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger id="status" className="text-right" data-testid="status-select">
                  <SelectValue placeholder="اختر الحالة" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">جديدة</SelectItem>
                  <SelectItem value="filed">مرفوعة</SelectItem>
                  <SelectItem value="hearings">جلسات</SelectItem>
                  <SelectItem value="final_judgment">حكم نهائي</SelectItem>
                  <SelectItem value="execution">تنفيذ</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newStatus === 'final_judgment' && (
              <div className="space-y-2">
                <Label htmlFor="judgment" className="text-right block">تفاصيل الحكم *</Label>
                <Textarea
                  id="judgment"
                  value={judgmentDetails}
                  onChange={(e) => setJudgmentDetails(e.target.value)}
                  rows={4}
                  className="text-right"
                  placeholder="أدخل تفاصيل الحكم النهائي..."
                  data-testid="judgment-details-input"
                />
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={handleUpdateStatus}
                disabled={updating || !newStatus || (newStatus === 'final_judgment' && !judgmentDetails)}
                className="flex-1 bg-[#E94B7B] hover:bg-[#d43a6a]"
                data-testid="submit-update-button"
              >
                {updating ? 'جارٍ التحديث...' : 'تحديث'}
              </Button>
              <Button variant="outline" onClick={() => setIsUpdateDialogOpen(false)} className="flex-1">
                إلغاء
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
